import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, TrendingDown, TrendingUp, Ban, Target, Sparkle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const linkFor = (n) => {
  if (!n.item_id) return null;
  return n.item_type === "vehicle"
    ? `/vehicles/${encodeURIComponent(n.item_id)}`
    : `/homes/${encodeURIComponent(n.item_id)}`;
};

export default function NotificationBell() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const loadCount = useCallback(async () => {
    try { const r = await api.get("/notifications/unread-count"); setCount(r.data.count); } catch {}
  }, []);

  useEffect(() => {
    if (!user) { setCount(0); return; }
    loadCount();
    const t = setInterval(loadCount, 60000);
    return () => clearInterval(t);
  }, [user, loadCount]);

  const loadList = async () => {
    try { const r = await api.get("/notifications"); setItems(r.data); } catch {}
  };

  // Opening the bell marks everything read automatically.
  const onOpen = async (v) => {
    setOpen(v);
    if (!v) return;
    await loadList();
    try { await api.post("/notifications/read-all"); setCount(0); } catch {}
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full relative" data-testid="notification-bell">
          <Bell className="w-4 h-4" />
          {count > 0 && (
            <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-mono-data flex items-center justify-center">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-display font-medium text-sm">Notifications</span>
        </div>
        <div className="max-h-[380px] overflow-y-auto overscroll-contain" data-testid="notification-scroll">
          {items.length ? (
            <div className="divide-y divide-border">
              {items.map((n) => {
                const to = linkFor(n);
                const body = (
                  <div className={`px-4 py-3 flex gap-3 ${n.read ? "opacity-60" : ""} ${to ? "hover:bg-secondary/40 transition-colors" : ""}`} data-testid="notification-item">
                    <div className="mt-0.5">
                      {n.type === "price_drop" ? <TrendingDown className="w-4 h-4 text-rose-500" /> : n.type === "price_increase" ? <TrendingUp className="w-4 h-4 text-emerald-500" /> : n.type === "off_market" ? <Ban className="w-4 h-4 text-muted-foreground" /> : n.type === "target_hit" ? <Target className="w-4 h-4 text-accent" /> : <Sparkle className="w-4 h-4 text-accent" />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium leading-tight">{n.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{n.message}</div>
                    </div>
                  </div>
                );
                return to
                  ? <Link key={n.id} to={to} onClick={() => setOpen(false)} className="block">{body}</Link>
                  : <div key={n.id}>{body}</div>;
              })}
            </div>
          ) : (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
              No notifications yet. Add homes or vehicles to your Watchlist — we'll alert you here when the price drops.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
